import * as _ from 'lodash';
import { AttachmentStorage } from './attachment-storage';
import { Injectable } from '@angular/core';
import { LogoutSubscriber } from 'shared/services/subscribers/logout-subscriber.service';

/**
 * A singleton class that is keeping references to the attachment storages of the e-mail work items.
 */
@Injectable({
  providedIn: 'root'
})
export class AttachmentStorageService {
  private _storages;

  constructor(LogoutSubscriber: LogoutSubscriber) {
    this._storages = {};
    LogoutSubscriber.subscribe(this._onLogout.bind(this));
  }

  /**
   * Creates a new attachment storage for the specified work item, or returns a previously created one.
   * @param {string} workItemId - The unique, internal, static identifier of the parent work item.
   * @returns {AttachmentStorage} The storage instance for the specified work item.
   * @throws {Error} If the work item identifier is not specified.
   */
  createOrGetStorage(workItemId) {
    if (!workItemId) {
      throw new Error('The work item ID must be specified when requesting an attachment storage!');
    }

    if (!this._storages[workItemId]) {
      this._storages[workItemId] = new AttachmentStorage();
    }

    return this._storages[workItemId];
  }

  /**
   * Deletes the attachment storage of the specified work item.
   * @param {string} workItemId - The unique, internal, static identifier of the parent work item.
   * @throws {Error} If the work item identifier is not specified.
   */
  deleteStorage(workItemId) {
    this.clearStorage(workItemId);

    delete this._storages[workItemId];
  }

  /**
   * Clears the attachment storage of the specified work item.
   * @param {string} workItemId - The unique, internal, static identifier of the parent work item.
   * @throws {Error} If the work item identifier is not specified.
   */
  clearStorage(workItemId) {
    if (!workItemId) {
      throw new Error('The work item ID must be specified when clearing an attachment storage!');
    }
    const storage = this._storages[workItemId];
    if (storage) {
      storage.clear();
    }
  }

  /**
   * Event handler that is called when the user logs out from the application.
   * @private
   */
  _onLogout() {
    _.forOwn(this._storages, (storage, workItemId) => {
      this.deleteStorage(workItemId);
    });
  }
}
